import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { nationsService, participantsService } from "../../services";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AdminModalTailwind from "./AdminModalTailwind";
import GenericSelectTailwind from "./GenericSelectTailwind";
import Toast from "./Toast";

function NationsTailwind() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [nations, setNations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Team-Management State
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [selectedNation, setSelectedNation] = useState(null);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [teamSearchTerm, setTeamSearchTerm] = useState("");
  const [selectedSearchTerm, setSelectedSearchTerm] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });

  // Sortier-State
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState("all");

  const MIN_TEAM_MEMBERS = 6;
  const MAX_TEAM_MEMBERS = 12;

  // Lade Daten beim ersten Laden
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Lade Nationen
      // Nutze aggregierte Backend-Statistik statt N+1-Requests
      const statsResponse = await nationsService.getParticipantStats();
      const nationsResponse = await nationsService.getAllNations();

      if (statsResponse.success && nationsResponse.success) {
        const statsMap = new Map(
          (statsResponse.data?.nations || []).map(n => [n.code, n.participantCount])
        );
        const merged = nationsResponse.data.map(nation => ({
          ...nation,
          participants: statsMap.get(nation.code) || 0,
          // Stelle sicher, dass participatesInTeamCompetition vorhanden ist
          participatesInTeamCompetition: nation.participatesInTeamCompetition || nation.participates_in_team_competition || false
        }));
        setNations(merged);
      } else {
        setError(nationsResponse.error);
      }
    } catch (err) {
      setError("Fehler beim Laden der Daten");
      console.error("Fehler beim Laden der Daten:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedFilter("all");
  };

  // Get active filters for display
  const getActiveFilters = () => {
    const active = [];
    if (selectedFilter !== "all") {
      const filterLabel = filterOptions.find(f => f.value === selectedFilter)?.label || selectedFilter;
      active.push({ type: "Status", value: filterLabel, onRemove: () => setSelectedFilter("all") });
    }
    if (searchTerm.trim() !== "") {
      active.push({ type: "Suche", value: searchTerm, onRemove: () => setSearchTerm("") });
    }
    return active;
  };

  const filterOptions = [
    { value: "all", label: t("admin.nations.filters.all", "Alle") },
    { value: "confirmed", label: t("admin.nations.filters.confirmed", "Bestätigt") },
    { value: "pending", label: t("admin.nations.filters.pending", "Ausstehend") }
  ];

  // Sortier-Handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  // Filter-, Such- und Sortierlogik
  const filteredNations = nations
    .filter((nation) => {
      const matchesSearch = nation.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        selectedFilter === "all" ||
        (selectedFilter === "confirmed" && nation.status?.toLowerCase() === "bestätigt") ||
        (selectedFilter === "pending" && nation.status?.toLowerCase() === "ausstehend");
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let aVal, bVal;
      if (sortField === "participants") {
        aVal = a.participants || 0;
        bVal = b.participants || 0;
      } else if (sortField === "status") {
        aVal = a.status || "";
        bVal = b.status || "";
      } else {
        aVal = a.name || "";
        bVal = b.name || "";
      }
      if (typeof aVal === "number") {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }
      return sortDirection === "asc"
        ? aVal.localeCompare(bVal, "de")
        : bVal.localeCompare(aVal, "de");
    });

  // KPI Berechnungen
  const calculateKPIs = () => {
    if (filteredNations.length === 0) {
      return {
        totalCount: 0,
        largestNation: null,
        averageParticipants: 0
      };
    }

    // Anzahl Nationen
    const totalCount = filteredNations.length;

    // Größte Nation (bei mehreren mit gleicher Anzahl eine beliebige)
    const largestNation = filteredNations.reduce((max, nation) => {
      const participants = nation.participants || 0;
      const maxParticipants = max.participants || 0;
      return participants > maxParticipants ? nation : max;
    }, filteredNations[0]);

    // Durchschnittliche Anzahl Teilnehmer pro Nation
    const totalParticipants = filteredNations.reduce((sum, nation) => sum + (nation.participants || 0), 0);
    const averageParticipants = totalCount > 0 ? Math.round((totalParticipants / totalCount) * 10) / 10 : 0;

    return {
      totalCount,
      largestNation,
      averageParticipants
    };
  };

  const kpis = calculateKPIs();

  // Pagination Logic
  const totalItems = filteredNations.length;
  const itemsPerPageNum = itemsPerPage === "all" ? totalItems : (typeof itemsPerPage === "string" ? parseInt(itemsPerPage) : itemsPerPage);
  const totalPages = itemsPerPage === "all" ? 1 : Math.ceil(totalItems / itemsPerPageNum);
  const startIndex = itemsPerPage === "all" ? 0 : (currentPage - 1) * itemsPerPageNum;
  const endIndex = itemsPerPage === "all" ? totalItems : startIndex + itemsPerPageNum;
  const paginatedNations = itemsPerPage === "all"
    ? filteredNations
    : filteredNations.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedFilter, itemsPerPage]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value === "all" ? "all" : parseInt(value));
    setCurrentPage(1);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Navigation zur Teilnehmerverwaltung mit Nationenvorwahl
  const handleShowParticipants = (nationCode) => {
    navigate(`/admin/participants?nation=${nationCode}`);
  };

  // Toast-Funktionen
  const addToast = (type, message) => {
    setToast({ show: true, message, type });

    // Toast nach 5 Sekunden automatisch entfernen
    setTimeout(() => {
      setToast({ show: false, message: "", type: "info" });
    }, 5000);
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "info" });
  };

  // Available Participants Query (für Team-Management)
  const availableParticipantsQuery = useQuery({
    queryKey: ['availableParticipants', selectedNation?.id],
    queryFn: async () => {
      if (!selectedNation?.id) return [];
      const res = await nationsService.getAvailableParticipants(selectedNation.id);
      if (!res.success) throw new Error(res.error || 'Fehler beim Laden der Teilnehmer');
      return res.data || [];
    },
    enabled: !!selectedNation?.id && teamModalOpen
  });

  // Team Query (für angezeigte Team-Größe)
  const teamQuery = useQuery({
    queryKey: ['team', selectedNation?.id],
    queryFn: async () => {
      if (!selectedNation?.id) return null;
      const res = await nationsService.getNationTeam(selectedNation.id);
      if (!res.success) throw new Error(res.error || 'Fehler beim Laden des Teams');
      return res.data;
    },
    enabled: !!selectedNation?.id && teamModalOpen
  });

  // Mutation für Team-Teilnahme
  const updateTeamParticipationMutation = useMutation({
    mutationFn: async ({ nationId, participatesInTeamCompetition }) => {
      const res = await nationsService.updateTeamParticipation(nationId, participatesInTeamCompetition);
      if (!res.success) throw new Error(res.error || 'Fehler beim Aktualisieren der Team-Teilnahme');
      return res.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['nations']);
      loadData(); // Lade Nationen neu
      const nationName = nations.find(n => n.id === variables.nationId)?.name || 'Nation';
      if (variables.participatesInTeamCompetition) {
        addToast('success', `Team-Teilnahme für "${nationName}" erfolgreich aktiviert`);
      } else {
        addToast('success', `Team-Teilnahme für "${nationName}" erfolgreich deaktiviert`);
      }
    },
    onError: (error, variables) => {
      const nationName = nations.find(n => n.id === variables.nationId)?.name || 'Nation';
      addToast('error', `Fehler beim Aktualisieren der Team-Teilnahme für "${nationName}": ${error.message}`);
    }
  });

  // Mutation für Team-Mitglieder setzen
  const setTeamMembersMutation = useMutation({
    mutationFn: async ({ nationId, participantIds }) => {
      const res = await nationsService.setTeamMembers(nationId, participantIds);
      if (!res.success) throw new Error(res.error || 'Fehler beim Speichern der Team-Mitglieder');
      return res.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['team', selectedNation?.id]);
      queryClient.invalidateQueries(['availableParticipants', selectedNation?.id]);
      const nationName = selectedNation?.name || 'Nation';
      const teamSize = variables.participantIds.length;
      const wasExistingTeam = selectedTeamMembers.length > 0 && teamQuery.data?.teamMembers?.length > 0;
      setTeamModalOpen(false);
      addToast('success', `Team für "${nationName}" erfolgreich ${wasExistingTeam ? 'aktualisiert' : 'erstellt'} (${teamSize} Mitglieder)`);
    },
    onError: (error) => {
      const nationName = selectedNation?.name || 'Nation';
      addToast('error', `Fehler beim Speichern des Teams für "${nationName}": ${error.message}`);
    }
  });

  // Team-Management Handler
  const handleOpenTeamModal = (nation) => {
    setSelectedNation(nation);
    setTeamModalOpen(true);
    setSelectedTeamMembers([]); // Wird via useEffect aktualisiert, sobald teamQuery.data geladen ist
  };

  const handleCloseTeamModal = () => {
    setTeamModalOpen(false);
    setSelectedNation(null);
    setSelectedTeamMembers([]);
  };

  const handleToggleTeamParticipation = async (nation, checked) => {
    try {
      await updateTeamParticipationMutation.mutateAsync({
        nationId: nation.id,
        participatesInTeamCompetition: checked
      });
      // Bei Aktivierung: Team-Modal direkt öffnen zum Hinzufügen der Teilnehmer
      if (checked) {
        handleOpenTeamModal(nation);
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Team-Teilnahme:', error);
      // Toast wird bereits in onError der Mutation angezeigt
    }
  };

  const handleToggleTeamMember = (participantId) => {
    setSelectedTeamMembers(prev => {
      if (prev.includes(participantId)) {
        return prev.filter(id => id !== participantId);
      } else {
        return [...prev, participantId];
      }
    });
  };

  const handleSaveTeamMembers = async () => {
    if (selectedTeamMembers.length < MIN_TEAM_MEMBERS) {
      addToast('warning', `Ein Team muss mindestens ${MIN_TEAM_MEMBERS} Mitglieder haben`);
      return;
    }
    if (selectedTeamMembers.length > MAX_TEAM_MEMBERS) {
      addToast('warning', `Ein Team kann maximal ${MAX_TEAM_MEMBERS} Mitglieder haben`);
      return;
    }
    try {
      await setTeamMembersMutation.mutateAsync({
        nationId: selectedNation.id,
        participantIds: selectedTeamMembers
      });
    } catch (error) {
      console.error('Fehler beim Speichern der Team-Mitglieder:', error);
      // Toast wird bereits in onError der Mutation angezeigt
    }
  };

  // Aktualisiere selectedTeamMembers wenn Team-Daten geladen werden
  useEffect(() => {
    if (teamQuery.data?.teamMembers && teamModalOpen) {
      setSelectedTeamMembers(teamQuery.data.teamMembers.map(tm => tm.participant.id));
    }
  }, [teamQuery.data, teamModalOpen]);

  if (loading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded w-56"></div>
              <div className="h-6 w-10 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
            </div>
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-64"></div>
          </div>

          {/* Controls Skeleton */}
          <div className="mb-6 flex items-center justify-between gap-3 animate-pulse">
            <div className="flex items-center gap-2">
              <div className="w-16 h-5 bg-slate-200 dark:bg-slate-700 rounded"></div>
              <div className="w-20 h-8 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
              <div className="w-24 h-8 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
              <div className="w-18 h-8 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
            </div>
            <div className="w-28 h-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
          </div>

          {/* Filter Section Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            {/* Search Bar Skeleton */}
            <div className="mb-6">
              <div className="h-[46px] bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
            </div>

            {/* Filter Dropdowns Skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div>
                <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-16 mb-2"></div>
                <div className="h-[46px] bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
              </div>
            </div>
          </div>

          {/* KPI Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 animate-pulse">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="h-3 w-24 bg-slate-200 dark:bg-slate-700 rounded mb-2"></div>
                    <div className="h-7 w-28 bg-slate-200 dark:bg-slate-700 rounded mb-2"></div>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-slate-200 dark:bg-slate-700"></div>
                </div>
              </div>
            ))}
          </div>

          {/* Table Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden animate-pulse">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    {[...Array(7)].map((_, i) => (
                      <th key={i} className="px-6 py-3">
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20"></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {[...Array(8)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(7)].map((_, j) => (
                        <td key={j} className="px-6 py-4">
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                {/* Pagination Footer Skeleton */}
                <tfoot className="bg-slate-50 dark:bg-slate-700/30">
                  <tr>
                    <td colSpan={7} className="px-6 py-4">
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-12"></div>
                          <div className="h-9 w-20 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24"></div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-32"></div>
                          <div className="h-9 w-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                          <div className="flex items-center gap-2">
                            <div className="h-9 w-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                            {[...Array(5)].map((_, i) => (
                              <div key={i} className="h-9 w-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                            ))}
                            <div className="h-9 w-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                  <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
                </div>
                <p className="text-slate-600 dark:text-slate-400 font-medium mb-4">{error}</p>
                <button
                  onClick={loadData}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-dark dark:bg-primary-light dark:hover:bg-primary text-white text-sm font-medium rounded-xl transition-colors shadow-sm"
                >
                  {t("admin.retry", "Erneut versuchen")}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t("admin.nations.title", "Nationenverwaltung")}
                </h1>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
                  <i className="fas fa-flag text-[10px]"></i>
                  {filteredNations.length}
                </span>
              </div>
              <p className="text-slate-500 dark:text-slate-400 mt-1.5 text-sm">
                {t("admin.nations.subtitle", "Verwalte alle Nationen und deren Teilnehmer.")}
              </p>
            </div>
          </div>
        </div>

        {/* Controls Section */}
        <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
          {/* Sortierung */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest whitespace-nowrap">
              Sortieren:
            </span>
            {[
              { field: "name", label: "Name" },
              { field: "participants", label: "Teilnehmer" },
              { field: "status", label: "Status" },
            ].map(({ field, label }) => (
              <button
                key={field}
                onClick={() => handleSort(field)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-xl border transition-colors ${
                  sortField === field
                    ? "bg-primary/8 dark:bg-primary-light/10 text-primary dark:text-primary-light border-primary/20 dark:border-primary-light/20"
                    : "bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"
                }`}
              >
                {label}
                <i className={`fas text-[9px] ${
                  sortField === field
                    ? sortDirection === "asc" ? "fa-sort-up" : "fa-sort-down"
                    : "fa-sort opacity-40"
                }`}></i>
              </button>
            ))}
          </div>

          {/* Hinzufügen (noch nicht implementiert) */}
          <button
            disabled
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-xl opacity-40 cursor-not-allowed"
            title="Nationen werden über den Import verwaltet"
          >
            <i className="fas fa-plus text-xs"></i>
            {t("admin.nations.add", "Hinzufügen")}
          </button>
        </div>

        {/* Filter Section */}
        <div id="filter-section" className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 mb-6">
          {/* Search Bar */}
          <div className="mb-5">
            <div className="relative">
              <input
                type="text"
                placeholder={t("admin.nations.search", "Nation suchen…")}
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 h-10 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors"
              />
              <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-xs"></i>
            </div>
          </div>

          {/* Filter Dropdowns */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            {/* Status Filter */}
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                {t("admin.nations.filters.title", "Status")}
              </label>
              <GenericSelectTailwind
                value={selectedFilter}
                onChange={handleFilterChange}
                options={filterOptions.map(opt => ({
                  value: opt.value,
                  label: opt.label
                }))}
                placeholder="Alle Status"
              />
            </div>
          </div>

          {/* Active Filters Section */}
          {getActiveFilters().length > 0 && (
            <div className="border-t border-slate-100 dark:border-slate-700/60 pt-4 mt-1">
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  Aktive Filter
                </span>
                <button
                  onClick={handleResetFilters}
                  className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-xs font-medium transition-colors border bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-600 hover:text-primary dark:hover:text-primary-light"
                >
                  <i className="fas fa-undo text-[10px]"></i>
                  Zurücksetzen
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {getActiveFilters().map((filter, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/8 dark:bg-primary-light/10 text-primary dark:text-primary-light border border-primary/20 dark:border-primary-light/20"
                  >
                    {filter.type}: {filter.value}
                    <button onClick={filter.onRemove} className="ml-0.5 opacity-60 hover:opacity-100 transition-opacity">
                      <i className="fas fa-times text-[9px]"></i>
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Anzahl Nationen */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Anzahl Nationen
              </p>
              <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
                {kpis.totalCount}
              </p>
            </div>
            <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
              <i className="fas fa-flag text-lg"></i>
            </div>
          </div>

          {/* Größte Nation */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Größte Nation
              </p>
              <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
                {kpis.largestNation ? kpis.largestNation.participants || 0 : 0}
              </p>
              {kpis.largestNation && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {kpis.largestNation.name}
                </p>
              )}
            </div>
            <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
              <i className="fas fa-users text-lg"></i>
            </div>
          </div>

          {/* Durchschnittliche Anzahl Teilnehmer pro Nation */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Ø Teilnehmer pro Nation
              </p>
              <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
                {kpis.averageParticipants}
              </p>
            </div>
            <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400">
              <i className="fas fa-chart-line text-lg"></i>
            </div>
          </div>
        </div>

        {/* Table */}
        {filteredNations.length > 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t("admin.nations.table.id", "ID")}
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none group"
                      onClick={() => handleSort("name")}
                    >
                      <span className={`inline-flex items-center gap-1.5 transition-colors ${sortField === "name" ? "text-primary dark:text-primary-light" : "text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200"}`}>
                        {t("admin.nations.table.name", "Name")}
                        <i className={`fas text-[9px] ${sortField === "name" ? (sortDirection === "asc" ? "fa-sort-up" : "fa-sort-down") : "fa-sort opacity-30"}`}></i>
                      </span>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t("admin.nations.table.code", "Code")}
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none group"
                      onClick={() => handleSort("participants")}
                    >
                      <span className={`inline-flex items-center gap-1.5 transition-colors ${sortField === "participants" ? "text-primary dark:text-primary-light" : "text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200"}`}>
                        {t("admin.nations.table.participants", "Teilnehmer")}
                        <i className={`fas text-[9px] ${sortField === "participants" ? (sortDirection === "asc" ? "fa-sort-up" : "fa-sort-down") : "fa-sort opacity-30"}`}></i>
                      </span>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none group"
                      onClick={() => handleSort("status")}
                    >
                      <span className={`inline-flex items-center gap-1.5 transition-colors ${sortField === "status" ? "text-primary dark:text-primary-light" : "text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200"}`}>
                        {t("admin.nations.table.status", "Status")}
                        <i className={`fas text-[9px] ${sortField === "status" ? (sortDirection === "asc" ? "fa-sort-up" : "fa-sort-down") : "fa-sort opacity-30"}`}></i>
                      </span>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t("admin.nations.table.teamCompetition", "Gruppenwettbewerb")}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t("admin.nations.table.actions", "Aktionen")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                  {paginatedNations.map((nation) => (
                    <tr key={nation.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-500 dark:text-slate-400">
                        {nation.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <img
                            src={process.env.PUBLIC_URL + "/images/flags/" + nation.code + ".png"}
                            alt={nation.name}
                            className="w-6 h-6 object-cover rounded-full border border-slate-200 dark:border-slate-600 flex-shrink-0"
                          />
                          <div>
                            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{nation.name}</div>
                            {nation.description && (
                              <div className="text-xs text-slate-500 dark:text-slate-400">{nation.description}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-mono font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-full border border-slate-200 dark:border-slate-600/50">
                          {nation.code}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-slate-800 dark:text-slate-100">
                          {nation.participants}
                          <span className="text-xs font-normal text-slate-400 dark:text-slate-500">Athl.</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${nation.status?.toLowerCase() === 'bestätigt'
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-700/30 dark:border-emerald-300/30'
                          : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border border-yellow-700/30 dark:border-yellow-300/30'
                          }`}>
                          {nation.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          <label className="relative inline-flex items-center cursor-pointer group">
                            <input
                              type="checkbox"
                              checked={nation.participatesInTeamCompetition || false}
                              onChange={(e) => handleToggleTeamParticipation(nation, e.target.checked)}
                              disabled={updateTeamParticipationMutation.isLoading}
                              className="sr-only"
                            />
                            <div className={`w-8 h-4 rounded-full transition-all duration-200 ${
                              nation.participatesInTeamCompetition
                                ? 'bg-primary dark:bg-primary-light'
                                : 'bg-slate-200 dark:bg-slate-600'
                            } ${updateTeamParticipationMutation.isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                              <div className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                                nation.participatesInTeamCompetition ? 'translate-x-4' : 'translate-x-0'
                              }`} />
                            </div>
                          </label>
                          {nation.participatesInTeamCompetition && (
                            <button
                              onClick={() => handleOpenTeamModal(nation)}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium text-primary dark:text-primary-light bg-primary/8 dark:bg-primary-light/10 hover:bg-primary/15 dark:hover:bg-primary-light/20 rounded-lg border border-primary/20 dark:border-primary-light/20 transition-colors"
                              title={t("admin.nations.actions.manageTeam", "Team verwalten")}
                            >
                              <i className="fas fa-users text-[9px]"></i>
                              Team
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            disabled
                            className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-400 dark:text-slate-500 opacity-40 cursor-not-allowed"
                            title={t("admin.nations.actions.edit", "Bearbeiten")}
                          >
                            <i className="fas fa-edit text-[11px]"></i>
                          </button>
                          <button
                            onClick={() => handleShowParticipants(nation.code)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:text-primary dark:hover:text-primary-light hover:border-primary/30 dark:hover:border-primary-light/30 transition-colors"
                            title={t("admin.nations.actions.view", "Teilnehmer anzeigen")}
                          >
                            <i className="fas fa-eye text-[11px]"></i>
                          </button>
                          <button
                            disabled
                            className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-400 dark:text-slate-500 opacity-40 cursor-not-allowed"
                            title={t("admin.nations.actions.delete", "Löschen")}
                          >
                            <i className="fas fa-trash text-[11px]"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {/* Pagination Footer Row */}
                  <tr className="bg-slate-50 dark:bg-slate-700/30">
                    <td colSpan={7} className="px-6 py-4">
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        {/* Items per page selector */}
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-slate-600 dark:text-slate-400">
                            Zeige
                          </span>
                          <div className="relative" style={{ zIndex: teamModalOpen ? 1 : 100, pointerEvents: teamModalOpen ? 'none' : 'auto' }}>
                            <GenericSelectTailwind
                              value={itemsPerPage.toString()}
                              onChange={(value) => handleItemsPerPageChange(value === "all" ? "all" : parseInt(value))}
                              options={[
                                { value: "10", label: "10" },
                                { value: "20", label: "20" },
                                { value: "50", label: "50" },
                                { value: "100", label: "100" },
                                { value: "all", label: "Alle" }
                              ]}
                              placeholder="Alle"
                              openUpward={true}
                              whiteBackground={true}
                              disabled={teamModalOpen}
                            />
                          </div>
                          <span className="text-sm text-slate-600 dark:text-slate-400">
                            Einträge pro Seite
                          </span>
                        </div>

                        {/* Pagination Info and Controls */}
                        <div className="flex items-center gap-4">
                          {/* Info Text */}
                          <span className="text-sm text-slate-600 dark:text-slate-400">
                            Zeige <span className="font-semibold text-slate-900 dark:text-slate-100">{startIndex + 1}</span>-<span className="font-semibold text-slate-900 dark:text-slate-100">{Math.min(endIndex, totalItems)}</span> von <span className="font-semibold text-slate-900 dark:text-slate-100">{totalItems}</span> Nationen
                          </span>

                          {/* Scroll to Top Button */}
                          <button
                            onClick={scrollToTop}
                            className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                            title="Nach oben scrollen"
                          >
                            <i className="fas fa-arrow-up text-xs"></i>
                          </button>

                          {/* Pagination Buttons */}
                          {itemsPerPage !== "all" && totalPages > 1 && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <i className="fas fa-chevron-left text-xs"></i>
                              </button>

                              {/* Page Numbers */}
                              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                  pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                  pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                  pageNum = totalPages - 4 + i;
                                } else {
                                  pageNum = currentPage - 2 + i;
                                }

                                return (
                                  <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    className={`inline-flex items-center justify-center w-9 h-9 rounded-xl border transition-colors ${currentPage === pageNum
                                      ? 'bg-primary dark:bg-primary-light text-white border-primary/30 dark:border-primary-light/30'
                                      : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                                      }`}
                                  >
                                    {pageNum}
                                  </button>
                                );
                              })}

                              <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <i className="fas fa-chevron-right text-xs"></i>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredNations.length === 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm text-center py-16 px-8">
            <div className="w-14 h-14 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-flag text-slate-400 dark:text-slate-500 text-xl"></i>
            </div>
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-1.5">Keine Nationen gefunden</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
              {searchTerm || selectedFilter !== "all"
                ? "Versuche die Filter oder den Suchbegriff anzupassen."
                : "Es sind noch keine Nationen vorhanden."
              }
            </p>
          </div>
        )}

        {/* Team-Management Modal */}
        <AdminModalTailwind
          isOpen={teamModalOpen}
          onClose={handleCloseTeamModal}
          title={`Team-Verwaltung: ${selectedNation?.name || ''}`}
          subtitle={`Wählen Sie ${MIN_TEAM_MEMBERS}-${MAX_TEAM_MEMBERS} Teilnehmer für das Team aus`}
          onSave={handleSaveTeamMembers}
          onCancel={handleCloseTeamModal}
          saveButtonText="Team speichern"
          cancelButtonText="Abbrechen"
          isLoading={availableParticipantsQuery.isLoading}
          isSaving={setTeamMembersMutation.isLoading}
          isSaveDisabled={
            selectedTeamMembers.length < MIN_TEAM_MEMBERS ||
            selectedTeamMembers.length > MAX_TEAM_MEMBERS
          }
        >
          <div className="space-y-4">
            {availableParticipantsQuery.isLoading ? (
              <div className="text-center py-10">
                <div className="inline-flex items-center justify-center w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-full mb-3">
                  <i className="fas fa-spinner fa-spin text-slate-500 dark:text-slate-400"></i>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Teilnehmer werden geladen…</p>
              </div>
            ) : availableParticipantsQuery.error ? (
              <div className="text-center py-10">
                <div className="inline-flex items-center justify-center w-10 h-10 bg-red-50 dark:bg-red-900/20 rounded-full mb-3">
                  <i className="fas fa-exclamation-triangle text-red-500 dark:text-red-400 text-sm"></i>
                </div>
                <p className="text-sm text-red-600 dark:text-red-400">
                  Fehler: {availableParticipantsQuery.error.message}
                </p>
              </div>
            ) : (
              <>
                {/* Team-Besetzungs-Status */}
                <div className="bg-slate-50/70 dark:bg-slate-700/30 rounded-xl border border-slate-200 dark:border-slate-700/60 px-4 py-3">
                  <div className="flex items-center justify-between mb-2.5">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                      Team-Besetzung
                    </span>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border transition-all ${
                      selectedTeamMembers.length >= MIN_TEAM_MEMBERS && selectedTeamMembers.length <= MAX_TEAM_MEMBERS
                        ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-600/50 text-emerald-700 dark:text-emerald-300"
                        : selectedTeamMembers.length > MAX_TEAM_MEMBERS
                          ? "bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-600/50 text-red-700 dark:text-red-300"
                          : "bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-600/50 text-amber-700 dark:text-amber-300"
                    }`}>
                      <i className={`fas text-[9px] ${
                        selectedTeamMembers.length >= MIN_TEAM_MEMBERS && selectedTeamMembers.length <= MAX_TEAM_MEMBERS
                          ? "fa-check" : selectedTeamMembers.length > MAX_TEAM_MEMBERS ? "fa-times" : "fa-users"
                      }`}></i>
                      {selectedTeamMembers.length} / {MAX_TEAM_MEMBERS}
                    </span>
                  </div>
                  <div className="relative h-1.5 w-full rounded-full bg-slate-200 dark:bg-slate-600 overflow-hidden mb-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-500 ${
                        selectedTeamMembers.length < MIN_TEAM_MEMBERS
                          ? "bg-amber-400 dark:bg-amber-500"
                          : selectedTeamMembers.length <= MAX_TEAM_MEMBERS
                            ? "bg-emerald-500"
                            : "bg-red-500 dark:bg-red-600"
                      }`}
                      style={{ width: `${Math.min(100, (selectedTeamMembers.length / MAX_TEAM_MEMBERS) * 100)}%` }}
                    />
                    {/* MIN-Marker */}
                    <div
                      className="absolute top-0 h-full w-px bg-slate-400/50 dark:bg-slate-400/30"
                      style={{ left: `${(MIN_TEAM_MEMBERS / MAX_TEAM_MEMBERS) * 100}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[10px] text-slate-400 dark:text-slate-500">
                    <span>0</span>
                    <span className="flex items-center gap-0.5">
                      <i className="fas fa-arrow-down text-[8px]"></i>
                      {MIN_TEAM_MEMBERS} Min.
                    </span>
                    <span>{MAX_TEAM_MEMBERS} Max.</span>
                  </div>
                  {selectedTeamMembers.length < MIN_TEAM_MEMBERS && (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1">
                      <i className="fas fa-info-circle text-[9px]"></i>
                      Noch {MIN_TEAM_MEMBERS - selectedTeamMembers.length} weitere Athleten erforderlich
                    </p>
                  )}
                  {selectedTeamMembers.length > MAX_TEAM_MEMBERS && (
                    <p className="text-[11px] text-red-600 dark:text-red-400 mt-2 flex items-center gap-1">
                      <i className="fas fa-exclamation-circle text-[9px]"></i>
                      Maximal {MAX_TEAM_MEMBERS} Athleten erlaubt
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Verfügbare Teilnehmer */}
                  <div className="flex flex-col gap-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
                        <i className="fas fa-users text-[9px]"></i>
                        Verfügbar
                      </span>
                      <span className="text-[11px] text-slate-400 dark:text-slate-500 tabular-nums">
                        {availableParticipantsQuery.data?.filter(p => !selectedTeamMembers.includes(p.id)).length || 0} Athleten
                      </span>
                    </div>

                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Suchen…"
                        value={teamSearchTerm}
                        onChange={(e) => setTeamSearchTerm(e.target.value)}
                        className="w-full pl-8 pr-3 h-9 text-sm text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500"
                      />
                      <i className="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-[11px]"></i>
                    </div>

                    <div className="max-h-[28rem] overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700/60 p-1.5 bg-white dark:bg-slate-800 space-y-1">
                      {availableParticipantsQuery.data?.filter(p => !selectedTeamMembers.includes(p.id)).length === 0 ? (
                        <div className="text-center py-10">
                          <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                            <i className="fas fa-user-slash text-slate-400 dark:text-slate-500 text-sm"></i>
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Keine weiteren Teilnehmer verfügbar
                          </p>
                        </div>
                      ) : (
                        availableParticipantsQuery.data
                          ?.filter((p) => !selectedTeamMembers.includes(p.id) && (p.name || "").toLowerCase().includes(teamSearchTerm.toLowerCase()))
                          .map((participant) => {
                            const reachedMax = selectedTeamMembers.length >= MAX_TEAM_MEMBERS;
                            return (
                              <div
                                key={participant.id}
                                onClick={() => !reachedMax && handleToggleTeamMember(participant.id)}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all ${reachedMax
                                  ? "bg-slate-50 dark:bg-slate-700/30 border-slate-200 dark:border-slate-700/50 opacity-50 cursor-not-allowed"
                                  : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700/50 hover:border-primary/40 dark:hover:border-primary-light/40 hover:shadow-sm cursor-pointer"
                                }`}
                                title={reachedMax ? "Maximale Teamgröße erreicht" : "Zum Team hinzufügen"}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                                    {participant.name}
                                  </div>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-[10px] text-slate-400 dark:text-slate-500 flex items-center gap-0.5">
                                      <i className="fas fa-id-badge text-[8px]"></i>
                                      {participant.participantNumber}
                                    </span>
                                    <span className="text-[10px] text-slate-400 dark:text-slate-500">{participant.age} J.</span>
                                    <span className={`text-[10px] flex items-center gap-0.5 ${participant.gender === 'weiblich' ? 'text-pink-400 dark:text-pink-500' : participant.gender === 'männlich' ? 'text-blue-400 dark:text-blue-500' : 'text-slate-400 dark:text-slate-500'}`}>
                                      <i className={`fas text-[8px] ${participant.gender === 'weiblich' ? 'fa-venus' : participant.gender === 'männlich' ? 'fa-mars' : 'fa-genderless'}`}></i>
                                      {participant.gender}
                                    </span>
                                  </div>
                                </div>
                                <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all ${reachedMax
                                  ? "border-slate-200 dark:border-slate-600 text-slate-300 dark:text-slate-600"
                                  : "border-primary/40 dark:border-primary-light/40 text-primary dark:text-primary-light hover:bg-primary hover:border-primary hover:text-white dark:hover:bg-primary-light dark:hover:border-primary-light dark:hover:text-slate-900"
                                }`}>
                                  <i className="fas fa-plus text-[9px]"></i>
                                </div>
                              </div>
                            );
                          })
                      )}
                    </div>
                  </div>

                  {/* Gewähltes Team */}
                  <div className="flex flex-col gap-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
                        <i className="fas fa-check-circle text-[9px]"></i>
                        Team
                      </span>
                      <span className="text-[11px] text-slate-400 dark:text-slate-500 tabular-nums">
                        {selectedTeamMembers.length} belegt
                      </span>
                    </div>

                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Suchen…"
                        value={selectedSearchTerm}
                        onChange={(e) => setSelectedSearchTerm(e.target.value)}
                        className="w-full pl-8 pr-3 h-9 text-sm text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500"
                      />
                      <i className="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-[11px]"></i>
                    </div>

                    <div className="max-h-[28rem] overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700/60 p-1.5 bg-slate-50/50 dark:bg-slate-700/20 space-y-1">
                      {selectedTeamMembers.length === 0 ? (
                        <div className="text-center py-10">
                          <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                            <i className="fas fa-users text-slate-400 dark:text-slate-500 text-sm"></i>
                          </div>
                          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                            Noch kein Athlet ausgewählt
                          </p>
                          <p className="text-[11px] text-slate-400 dark:text-slate-500">
                            Klicke links auf einen Athleten
                          </p>
                        </div>
                      ) : (
                        availableParticipantsQuery.data
                          ?.filter((p) => selectedTeamMembers.includes(p.id) && (p.name || "").toLowerCase().includes(selectedSearchTerm.toLowerCase()))
                          .map((participant) => (
                            <div
                              key={participant.id}
                              className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-emerald-200 dark:border-emerald-700/50 bg-emerald-50/60 dark:bg-emerald-900/10"
                            >
                              <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                                <i className="fas fa-check text-white text-[9px]"></i>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                                  {participant.name}
                                </div>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-[10px] text-slate-500 dark:text-slate-400 flex items-center gap-0.5">
                                    <i className="fas fa-id-badge text-[8px]"></i>
                                    {participant.participantNumber}
                                  </span>
                                  <span className="text-[10px] text-slate-500 dark:text-slate-400">{participant.age} J.</span>
                                  <span className={`text-[10px] flex items-center gap-0.5 ${participant.gender === 'weiblich' ? 'text-pink-400 dark:text-pink-500' : participant.gender === 'männlich' ? 'text-blue-400 dark:text-blue-500' : 'text-slate-400 dark:text-slate-500'}`}>
                                    <i className={`fas text-[8px] ${participant.gender === 'weiblich' ? 'fa-venus' : participant.gender === 'männlich' ? 'fa-mars' : 'fa-genderless'}`}></i>
                                    {participant.gender}
                                  </span>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleToggleTeamMember(participant.id)}
                                title="Aus Team entfernen"
                                className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center border border-slate-300 dark:border-slate-600 text-slate-400 dark:text-slate-500 hover:border-red-300 dark:hover:border-red-600 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                              >
                                <i className="fas fa-times text-[9px]"></i>
                              </button>
                            </div>
                          ))
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </AdminModalTailwind>

        {/* Toast */}
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.show}
          onClose={hideToast}
        />
      </div>
    </main>
  );
}

export default NationsTailwind;
